const MEASURE_TIMEOUT_MS = 4000

/**
 * Display-oriented pixel dimensions of an image blob, or null if they cannot
 * be read. Callers should omit the values rather than substitute a guess.
 *
 * Reads naturalWidth/naturalHeight from a decoded <img>, which browsers report
 * *after* applying EXIF orientation. An untouched rotated phone photo therefore
 * measures the way it is displayed rather than the way it is stored, which is
 * the whole point: a portrait photo that measured landscape would invert every
 * downstream decision made from it.
 *
 * Measure the blob being uploaded, not the file the guest picked. Anything that
 * re-encodes through a canvas (Compressor.js, the crop path) strips EXIF, so the
 * output's stored pixels are its displayed pixels unconditionally.
 *
 * Never rejects and never waits indefinitely — a guest's upload must not fail or
 * stall on an advisory measurement.
 */
/**
 * Natural pixel size of an image URL the crop editor is about to frame.
 * Accepts a remote URL or an object URL. Does not read pixels, so a CDN image
 * does not need CORS. Resolves null on failure or timeout.
 */
export function measureImageUrl(url, { timeoutMs = MEASURE_TIMEOUT_MS } = {}) {
  if (!url) return Promise.resolve(null)

  return new Promise((resolve) => {
    const image = new Image()
    let timer = null
    let settled = false

    const finish = (dimensions) => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      image.onload = null
      image.onerror = null
      resolve(dimensions)
    }

    image.onload = () => {
      const width = Math.round(image.naturalWidth || 0)
      const height = Math.round(image.naturalHeight || 0)
      finish(width > 0 && height > 0 ? { width, height } : null)
    }
    image.onerror = () => finish(null)
    timer = setTimeout(() => finish(null), timeoutMs)
    image.src = url
  })
}

export function measureImageDimensions(blob, { timeoutMs = MEASURE_TIMEOUT_MS } = {}) {
  if (!blob) return Promise.resolve(null)

  return new Promise((resolve) => {
    const image = new Image()
    let objectUrl = ''
    let timer = null
    let settled = false

    const finish = (dimensions) => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      image.onload = null
      image.onerror = null
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      resolve(dimensions)
    }

    image.onload = () => {
      const width = Math.round(image.naturalWidth || 0)
      const height = Math.round(image.naturalHeight || 0)
      finish(width > 0 && height > 0 ? { width, height } : null)
    }
    image.onerror = () => finish(null)
    timer = setTimeout(() => finish(null), timeoutMs)

    try {
      objectUrl = URL.createObjectURL(blob)
      image.src = objectUrl
    } catch {
      finish(null)
    }
  })
}

export const MEASURE_IMAGE_TIMEOUT_MS = MEASURE_TIMEOUT_MS
